/* PDP gallery: resize the slideshow-component to match the active slide's
   height instead of letting it stretch to the tallest sibling. The custom
   <slideshow-component> dispatches `slideshow:select` on every slide change.
   We hook that event and set an explicit height on the gallery container
   based on the now-active slide's measured height.

   Why this exists: each slide already has its aspect-ratio set per-image
   (via --media-preview-ratio), so a 1:1 packshot renders at e.g. 554x554
   and a 0.33 portrait at 554x800 (capped to viewport). Without this hook
   the carousel parent sizes to the tallest sibling and leaves dead space
   below the active slide whenever a shorter image is selected. */

const SELECTOR = '.product-information__media slideshow-component';

function syncSlideshowHeightToActive(slideshow, activeSlide) {
  if (!slideshow || !activeSlide) return;
  const slideHeight = activeSlide.getBoundingClientRect().height;
  if (slideHeight <= 0) return;

  // On mobile the thumbnails strip renders below the slide (no CSS grid layout
  // until 750px). The parent `.product-information__media` clips overflow, so
  // include the controls height here to keep the thumbnails visible.
  let controlsHeight = 0;
  if (window.matchMedia('(max-width: 749px)').matches) {
    const controls = slideshow.querySelector('slideshow-controls');
    if (controls) controlsHeight = controls.getBoundingClientRect().height;
  }

  slideshow.style.height = `${Math.round(slideHeight + controlsHeight)}px`;
}

function getActiveSlide(slideshow) {
  return (
    slideshow.querySelector('slideshow-slide[aria-hidden="false"]') ||
    slideshow.querySelector('slideshow-slide')
  );
}

function init(slideshow) {
  // Initial sync — wait for layout, then size to the first/active slide.
  requestAnimationFrame(() => {
    syncSlideshowHeightToActive(slideshow, getActiveSlide(slideshow));
  });

  // Re-sync on slide changes.
  slideshow.addEventListener('slideshow:select', (event) => {
    const slide = event.detail?.slide;
    if (slide) syncSlideshowHeightToActive(slideshow, slide);
  });

  // Re-sync on viewport resize (slide aspect-derived heights change).
  let resizeFrame = null;
  window.addEventListener('resize', () => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      syncSlideshowHeightToActive(slideshow, getActiveSlide(slideshow));
    });
  });

  // Re-sync once images finish loading (natural aspect can shift initial measure).
  slideshow.querySelectorAll('img').forEach((img) => {
    if (img.complete) return;
    img.addEventListener(
      'load',
      () => {
        syncSlideshowHeightToActive(slideshow, getActiveSlide(slideshow));
      },
      { once: true }
    );
  });
}

function bootstrap() {
  document.querySelectorAll(SELECTOR).forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
